import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabCompte from '../tabs/TabCompte';
import { apiGet, apiPost, apiPut, apiPatch } from '../utils/api-client';
import { translateApiError } from '../utils/errors';

/**
 * TabCompteContainer
 *
 * Endpoints branchés :
 *  - GET   /api/account/me                  → identité du compte
 *  - POST  /api/account/change-email        → ouvre une demande vérifiée
 *  - POST  /api/account/cancel-email-change → annule la demande en attente
 *  - PUT   /api/auth/profile                → identité + mot de passe
 *  - PATCH /api/account/preferred-lang      → langue de l'interface
 *
 * ⚠️ Le changement d'e-mail ne modifie rien immédiatement : il envoie un lien
 * de confirmation à la nouvelle adresse et une alerte à l'ancienne. Tant que le
 * lien n'est pas cliqué, la connexion se fait toujours avec l'adresse actuelle.
 */
export default function TabCompteContainer() {
  const { t, i18n } = useTranslation('tabs');

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState({ identity: false, email: false, password: false, lang: false });

  const flag = (key, value) => setSaving((s) => ({ ...s, [key]: value }));
  const ok = (message) => setFeedback({ type: 'success', message });
  const ko = (e, fallbackKey) => setFeedback({
    type: 'error',
    message: translateApiError(e?.payload) || e?.message || t(fallbackKey),
  });

  const load = useCallback(async () => {
    try {
      const data = await apiGet('/api/account/me');
      setAccount(data);
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || t('compte.errors.load') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleSaveIdentity = useCallback(async (ident) => {
    flag('identity', true);
    setFeedback(null);
    try {
      await apiPut('/api/auth/profile', {
        prenom: ident.prenom,
        nom: ident.nom,
        telephone: ident.telephone,
      });
      ok(t('compte.identity.saved'));
      await load();
    } catch (e) {
      ko(e, 'compte.errors.save');
    } finally {
      flag('identity', false);
    }
  }, [load, t]);

  const handleRequestEmailChange = useCallback(async ({ new_email, current_password }) => {
    flag('email', true);
    setFeedback(null);
    try {
      await apiPost('/api/account/change-email', {
        new_email,
        current_password,
        lang: i18n.language || 'fr',
      });
      ok(t('compte.email.request_sent', { email: new_email }));
      await load();
    } catch (e) {
      ko(e, 'compte.errors.save');
    } finally {
      flag('email', false);
    }
  }, [load, t, i18n.language]);

  const handleCancelEmailChange = useCallback(async () => {
    setFeedback(null);
    try {
      await apiPost('/api/account/cancel-email-change');
      ok(t('compte.email.change_cancelled'));
      await load();
    } catch (e) {
      ko(e, 'compte.errors.save');
    }
  }, [load, t]);

  const handleChangePassword = useCallback(async ({ current, next }) => {
    flag('password', true);
    setFeedback(null);
    try {
      await apiPut('/api/auth/profile', {
        mot_de_passe_actuel: current,
        nouveau_mot_de_passe: next,
      });
      ok(t('compte.password.saved'));
    } catch (e) {
      ko(e, 'compte.errors.save');
    } finally {
      flag('password', false);
    }
  }, [t]);

  const handleChangeLang = useCallback(async (lang) => {
    flag('lang', true);
    setFeedback(null);
    try {
      await apiPatch('/api/account/preferred-lang', { preferred_lang: lang });
      // Bascule immédiate de l'interface : sans cela, l'utilisateur devrait
      // recharger pour voir l'effet de son choix.
      await i18n.changeLanguage(lang);
      setAccount((a) => (a ? { ...a, preferred_lang: lang } : a));
      ok(t('compte.lang.saved'));
    } catch (e) {
      ko(e, 'compte.errors.save');
    } finally {
      flag('lang', false);
    }
  }, [t, i18n]);

  return (
    <TabCompte
      account={account}
      loading={loading}
      savingIdentity={saving.identity}
      savingEmail={saving.email}
      savingPassword={saving.password}
      savingLang={saving.lang}
      feedback={feedback}
      onSaveIdentity={handleSaveIdentity}
      onRequestEmailChange={handleRequestEmailChange}
      onCancelEmailChange={handleCancelEmailChange}
      onChangePassword={handleChangePassword}
      onChangeLang={handleChangeLang}
    />
  );
}
